import { useEffect, useRef } from 'react';

import { type CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import {
	hasProductPermission,
	useProductPermissions,
} from '@atlaskit/teams-app-internal-product-permissions';

import { triggerAnalyticsForContainerPermissions } from '../analytics';

function useContainerPermissionsAnalyticsEvent(
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	cloudId?: string,
	userId?: string,
) {
	const { loading, data, error } = useProductPermissions({
		userId,
		cloudId,
		permissionsToCheck: {
			jira: ['CREATE_PROJECT'],
			loom: ['write'],
			confluence: ['CREATE_SPACE'],
		},
	});
	const hasFiredRef = useRef(false);
	const { fireEvent } = useAnalyticsEvents();
	useEffect(() => {
		if (loading || !data || Object.keys(data).length === 0 || error || hasFiredRef.current) {
			return;
		}
		const attributes = {
			canCreateConfluenceContainer: data
				? hasProductPermission(data, 'confluence', ['CREATE_SPACE'])
				: false,
			canCreateLoomContainer: data ? hasProductPermission(data, 'loom', ['write']) : false,
			canCreateJiraContainer: data ? hasProductPermission(data, 'jira', ['CREATE_PROJECT']) : false,
		};
		if (fg('ptc-enable-teams-common-analytics-refactor')) {
			fireEvent('track.containerPermissions.succeeded', {
				canCreateConfluenceContainer: attributes.canCreateConfluenceContainer || false,
				canCreateLoomContainer: attributes.canCreateLoomContainer || false,
				canCreateJiraContainer: attributes.canCreateJiraContainer || false,
			});
		} else {
			triggerAnalyticsForContainerPermissions(createAnalyticsEvent, attributes);
		}
		hasFiredRef.current = true;
	}, [loading, data, error, createAnalyticsEvent, fireEvent]);
}

export { useContainerPermissionsAnalyticsEvent };
